import { createContext, useContext, useMemo, useState } from 'react';
import useLanguageActions from '../hooks/APIHooks/users/useLanguageActions';
import { Language } from '../types/languages';

// datatype of the context declared here
interface ILanguageContext {
  targetedLanguage: Language | null;
  languageList: Array<Language>;
  searchText: string;
  showDeleteModal: boolean;
  setTargetedLanguage: React.Dispatch<React.SetStateAction<Language | null>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

// creating the context here
const LanguageContext = createContext<ILanguageContext | null>(null);

const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [searchText, setSearchText] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [targetedLanguage, setTargetedLanguage] = useState<Language | null>(
    null
  );

  // integration of custom API hooks here
  const { getAddedLanguagesHandler } = useLanguageActions('addedLanguages');

  // setting languages into local state here
  const languageList = useMemo(() => {
    if (!getAddedLanguagesHandler.data?.is_success) return [];

    return getAddedLanguagesHandler.data.data.filter((language) =>
      searchText
        ? language.name.toLowerCase().includes(searchText.toLowerCase())
        : language
    );
  }, [getAddedLanguagesHandler.data, searchText]);

  // rendering the context provider here
  return (
    <LanguageContext.Provider
      value={{
        targetedLanguage,
        languageList,
        searchText,
        showDeleteModal,
        setTargetedLanguage,
        setSearchText,
        setShowDeleteModal,
        isError: getAddedLanguagesHandler.isError,
        isLoading: getAddedLanguagesHandler.isLoading,
        isSuccess: getAddedLanguagesHandler.isSuccess,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;

// custom hook to access the context declared here
export const useLanguageContext = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguageContext must be within LanguageProvider!');
  }

  return context;
};
